import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import nablLogo from '../../assets/img/nabl.png';
import icmrLogo from '../../assets/img/icmr.png';

const ACCRED_BADGES = [
  { code: 'NABL', sub: 'Accredited Laboratory', img: nablLogo, alt: 'NABL accreditation logo' },
  { code: 'ICMR', sub: 'Approved Laboratory',   img: icmrLogo, alt: 'ICMR approval logo' },
];

const TRUST_ROWS = [
  {
    heading: 'The science behind every result',
    tiles: [
      { color: '#FBBF24', bgRgb: '251,191,36', ico: '🔬', title: 'Pre-Sample QC Pass', desc: 'Daily Westgard multi-rule QC clears every analyzer before a single patient sample runs.', sub: 'Zero results released without QC clearance' },
      { color: '#A78BFA', bgRgb: '167,139,250', ico: '📏', title: 'Methods Traceability', desc: 'Every test method calibrated against international reference materials', sub: 'NIST/JCTLM-traceable methods' },
      { color: '#38BDF8', bgRgb: '56,189,248', ico: '🩸', title: 'One Sample, One Standard', desc: 'Whether collected at a centre or your doorstep, every sample is processed at the same NABL-accredited reference lab.', sub: 'Same lab, same protocol, same sign-off' },
    ],
  },
  {
    heading: 'The rigour behind every result',
    tiles: [
      { color: '#FB923C', bgRgb: '251,146,60', ico: '🎯', title: 'Daily Calibration', desc: 'Analyzers calibrated and verified at the start of every reporting shift, traceable to international standards.', sub: 'Per-shift calibration, CLSI EP-aligned' },
      { color: '#34D399', bgRgb: '52,211,153', ico: '🌐', title: 'EQA Proficiency Verified', desc: 'Blind-tested against 500+ global reference labs across 36 EQA cycles a year. Independent verification, not self-certification.', sub: 'Top 5% performance' },
      { color: '#22D3EE', bgRgb: '34,211,238', ico: '🔁', title: 'Zero-Tolerance Repeat Policy', desc: 'If any result looks off — even a borderline flag — we re-run the test free of cost before the report leaves the lab.', sub: 'Auto re-run, no questions asked' },
    ],
  },
  {
    heading: 'The accountability behind every report',
    tiles: [
      { color: '#F87171', bgRgb: '248,113,113', ico: '📞', title: 'Critical Value Callback', desc: 'Any result outside critical thresholds triggers a pathologist phone call within 30 minutes — to you, and to your doctor.', sub: '< 30 min escalation' },
      { color: '#C084FC', bgRgb: '192,132,252', ico: '🔐', title: 'Tamper-Proof Audit Trail', desc: 'Every step from collection to release is logged, QR-verified, and digitally signed. Your report is traceable to the second.', sub: 'Immutable chain of custody' },
      { color: '#A78BFA', bgRgb: '167,139,250', ico: '👨‍⚕️', title: 'Pathologist Sign', desc: 'Every report is reviewed and signed by an in-house pathologist before release. No algorithm acts alone.', sub: '100% pathologist verified' },
    ],
  },
];

const Accreditations = () => (
  <Box sx={{ bgcolor: 'var(--forest)', color: '#fff', py: { xs: 7, md: 10 } }}>
    <Container maxWidth="xl" sx={{ maxWidth: 1300, px: { xs: 2, md: 4 } }}>
      {/* Centered header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.25,
            mb: 1.75,
            lineHeight: 1,
          }}
        >
          <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage-l)' }} />
          <Typography
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--sage-l)',
              lineHeight: 1,
            }}
          >
            Certified & Accredited
          </Typography>
          <Box sx={{ width: 24, height: '1.5px', bgcolor: 'var(--sage-l)' }} />
        </Box>
        <Typography
          className="serif"
          sx={{
            fontSize: { xs: 24, sm: 28, md: 32, lg: 36 },
            fontWeight: 500,
            color: '#fff',
            lineHeight: 1.15,
            mb: 1.75,
          }}
        >
          You can trust our results
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 13, md: 14 },
            color: 'rgba(255,255,255,0.45)',
            fontWeight: 300,
            maxWidth: 560,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          Every result that leaves our lab passes through three layers of verification: a calibrated analyzer, a trained pathologist, and an auditable trail. We don't ask you to trust us — we show you why you can.
        </Typography>
      </Box>

      {/* NABL / ICMR badge strip */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: { xs: 1.75, md: 2.25 },
          maxWidth: 640,
          mx: 'auto',
        }}
      >
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.12)' }} />
        <Typography
          className="serif"
          sx={{
            fontSize: { xs: 15, md: 17 },
            fontWeight: 500,
            color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.01em',
            whiteSpace: { xs: 'normal', md: 'nowrap' },
            textAlign: 'center',
          }}
        >
          TrustLab holds major national accreditation for diagnostic lab quality
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.12)' }} />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2.5,
          mb: { xs: 4, md: 5 },
          maxWidth: 640,
          mx: 'auto',
        }}
      >
        {ACCRED_BADGES.map((b) => (
          <Box
            key={b.code}
            sx={{
              bgcolor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '16px',
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: 'rgba(61,122,82,0.18)',
                borderColor: 'rgba(200,237,216,0.3)',
              },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '14px',
                bgcolor: '#fff',
                border: '1px solid rgba(255,255,255,0.1)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 1,
                mb: 1.25,
                lineHeight: 1,
              }}
            >
              <Box
                component="img"
                src={b.img}
                alt={b.alt}
                sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
              />
            </Box>
            <Typography
              className="mono"
              sx={{
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: 'rgba(255,255,255,0.5)',
                mb: 0.5,
                lineHeight: 1,
              }}
            >
              {b.code} Accredited Laboratory
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.4)',
                fontWeight: 300,
                letterSpacing: '0.04em',
                lineHeight: 1,
              }}
            >
              {b.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Trust tiles — three row groups, each with a sub-heading + 3 tiles */}
      {TRUST_ROWS.map((row, rIdx) => (
        <Box key={row.heading} sx={{ mt: rIdx === 0 ? 0 : { xs: 4, md: 5 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: { xs: 1.75, md: 2.25 },
            }}
          >
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.12)' }} />
            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 15, md: 17 },
                fontWeight: 500,
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: '0.01em',
                whiteSpace: { xs: 'normal', md: 'nowrap' },
                textAlign: 'center',
              }}
            >
              {row.heading}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.12)' }} />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 2,
              width: '100%',
            }}
          >
            {row.tiles.map((t) => (
              <Box
                key={t.title}
                sx={{
                  bgcolor: `rgba(${t.bgRgb}, 0.08)`,
                  border: '1.5px solid',
                  borderColor: `rgba(${t.bgRgb}, 0.3)`,
                  borderTop: '4px solid',
                  borderTopColor: t.color,
                  borderRadius: '14px',
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 2.25, md: 2.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 1,
                  transition: 'all 0.28s',
                  minHeight: { md: 200 },
                  '&:hover': {
                    bgcolor: `rgba(${t.bgRgb}, 0.15)`,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: `rgba(${t.bgRgb}, 0.22)`,
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    lineHeight: 1,
                  }}
                >
                  {t.ico}
                </Box>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: 1.3,
                  }}
                >
                  {t.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,0.62)',
                    fontWeight: 300,
                    lineHeight: 1.55,
                    flex: 1,
                  }}
                >
                  {t.desc}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: t.color,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    lineHeight: 1.4,
                    mt: 0.25,
                  }}
                >
                  {t.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Container>
  </Box>
);

export default Accreditations;
